/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/
import { Outlet, useLocation } from '@tanstack/react-router'
import { getCookie } from '@/lib/cookies'
import { useAuthUserSync } from '@/lib/use-auth-user-sync'
import { cn } from '@/lib/utils'
import { LayoutProvider } from '@/context/layout-provider'
import { SearchProvider } from '@/context/search-provider'
import { SidebarInset, SidebarProvider } from '@/components/ui/sidebar'
import { AnimatedOutlet } from '@/components/page-transition'
import { SkipToMain } from '@/components/skip-to-main'
import { WorkspaceProvider } from '../context/workspace-context'
import { AppHeader } from './app-header'
import { AppSidebar } from './app-sidebar'

type AuthenticatedLayoutProps = {
  children?: React.ReactNode
}

/**
 * Routes that opt out of the standard app chrome (sidebar + topbar) and own
 * the full viewport themselves. The playground is a standalone two-pane
 * workspace with its own collapsible left panel + back-to-dashboard button,
 * so wrapping it in the main app shell would double up navigation.
 */
const CHROMELESS_ROUTES = ['/playground'] as const

function isChromelessRoute(pathname: string): boolean {
  return CHROMELESS_ROUTES.some(
    (route) => pathname === route || pathname.startsWith(`${route}/`)
  )
}

/**
 * Noya authenticated shell.
 *
 * Layout: full-height flex row.
 *   - White sidebar (210px) with teal active states
 *   - White 54px topbar pinned above page bg (#F4FAF9)
 *   - Content area scrollable, page bg color
 *
 * Chromeless routes (e.g. the playground) skip the sidebar + topbar entirely
 * and render full-screen; all other routes keep the standard shell unchanged.
 */
export function AuthenticatedLayout(props: AuthenticatedLayoutProps) {
  const defaultOpen = getCookie('sidebar_state') !== 'false'
  const { pathname } = useLocation()
  const chromeless = isChromelessRoute(pathname)

  // Keep the header/sidebar/wallet balance (auth.user.quota) fresh: refetch
  // /api/user/self on an interval + window focus so it never shows a stale
  // login-time snapshot after a topup/redemption.
  useAuthUserSync()

  // Full-screen standalone route: keep the auth/context providers (the page may
  // rely on system config, search, workspace context, etc.) but render only the
  // routed page across the entire viewport — no AppSidebar, no AppHeader.
  if (chromeless) {
    return (
      <LayoutProvider>
        <SearchProvider>
          <WorkspaceProvider>
            <SkipToMain />
            <div className='h-svh w-full overflow-hidden'>
              {props.children ?? <Outlet />}
            </div>
          </WorkspaceProvider>
        </SearchProvider>
      </LayoutProvider>
    )
  }

  return (
    <LayoutProvider>
      <SearchProvider>
        <WorkspaceProvider>
          {/*
           * SidebarProvider wraps the whole shell.
           * flex-col so AppHeader sits above the sidebar+content row.
           * min-h-svh ensures full viewport height.
           */}
          <SidebarProvider
            defaultOpen={defaultOpen}
            className='flex-col'
            style={
              {
                // Main sidebar width — 300px per design request.
                '--sidebar-width': '300px',
              } as React.CSSProperties
            }
          >
            <SkipToMain />
            {/* Noya topbar: white, 54px, full-width, sticky */}
            <AppHeader />
            <div className='flex min-h-0 w-full flex-1'>
              <AppSidebar />
              <SidebarInset
                className={cn(
                  '@container/content',
                  'bg-background',
                  'h-[calc(100svh-var(--app-header-height,0px))]',
                  'peer-data-[variant=inset]:h-[calc(100svh-var(--app-header-height,0px)-(var(--spacing)*4))]'
                )}
              >
                {props.children ?? <AnimatedOutlet />}
              </SidebarInset>
            </div>
          </SidebarProvider>
        </WorkspaceProvider>
      </SearchProvider>
    </LayoutProvider>
  )
}
